import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, DataTable, Select, money, statusTone, useFilterOptions, useTable, eurOf, useEurRate } from "./ui";
import { media } from "../../lib/api";
import { ZoomImage } from "../../components/ZoomImage";
import AdminProductEditor from "./AdminProductEditor";

const NewBouquetModal = ({ partners, onClose, onCreated }) => {
  const [f, setF] = useState({ title: "", cost_price_mad: "", base_price_eur: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fx = useEurRate();
  const margin = Number(f.base_price_eur || 0) - Number(f.cost_price_mad || 0) * (fx?.eur || 0);
  const create = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/admin/products", { ...f, cost_price_mad: Number(f.cost_price_mad || 0), base_price_eur: Number(f.base_price_eur || 0) });
      onCreated(data.id);
    } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 scrim z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-bouquet-modal">
      <div className="bg-plaster w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl text-ink">New bouquet</h3>
        <p className="text-xs text-muted">Create the draft, then fill photos, description and sizes in the editor.</p>
        <input className="field" placeholder="Bouquet name" data-testid="new-bouquet-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div className="grid sm:grid-cols-3 gap-2 items-end">
          <label className="block"><span className="smallcaps block">Cost MAD</span>
            <input type="number" min="0" step="0.01" className="field tabular" data-testid="new-bouquet-cost" value={f.cost_price_mad} onChange={(e) => setF({ ...f, cost_price_mad: e.target.value })} /><span className="text-xs text-muted" data-testid="new-bouquet-cost-eur">{eurOf(fx, f.cost_price_mad)}</span></label>
          <label className="block"><span className="smallcaps block">Selling EUR</span>
            <input type="number" min="0.01" step="0.01" className="field tabular" data-testid="new-bouquet-price" value={f.base_price_eur} onChange={(e) => setF({ ...f, base_price_eur: e.target.value })} /></label>
          <p className={`font-display text-xl tabular pb-2 ${margin <= 0 ? "text-danger" : "text-success"}`} data-testid="new-bouquet-margin">{f.base_price_eur && fx ? `€${margin.toFixed(2)}` : "—"}</p>
        </div>
        {msg ? <p className="text-sm text-danger" data-testid="new-bouquet-message">{msg}</p> : null}
        <div className="flex gap-2">
          <button className="btn-primary text-xs px-4" disabled={busy || !f.title || !f.base_price_eur} data-testid="new-bouquet-create" onClick={create}>Create & edit</button>
          <button className="btn-ghost text-xs px-4" data-testid="new-bouquet-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Sizes with no euro price are charged at a converted MAD price that drifts with the exchange rate.
const PriceHealthBanner = ({ count, active, onShow, onClear }) => {
  if (!count && !active) return null;
  return (
    <div role={count ? "alert" : "status"} data-testid="price-health-banner"
         className={`flex flex-wrap items-center justify-between gap-3 border px-4 py-3 ${count ? "border-brass/60 bg-brass/10" : "border-success/50 bg-success/10"}`}>
      <p className="text-sm text-ink flex items-start gap-2">
        {count
          ? <AlertTriangle size={16} className="text-brass shrink-0 mt-0.5" />
          : <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" />}
        <span data-testid="price-health-count">
          {count
            ? `${count} bouquet${count === 1 ? " has" : "s have"} a size without a euro price. Buyers are charged a converted dirham price that changes with the exchange rate — set a fixed € price for each size.`
            : "Every bouquet size has a euro price."}
        </span>
      </p>
      {active
        ? <button className="btn-ghost text-xs px-3" data-testid="price-health-clear" onClick={onClear}>Show all bouquets</button>
        : <button className="btn-primary text-xs px-3" data-testid="price-health-show" onClick={onShow}>Show them</button>}
    </div>
  );
};

const MissingEurBadge = ({ row }) => {
  const sizes = row.missing_eur_sizes || [];
  if (!sizes.length) return null;
  return <Badge tone="warn" testid={`missing-eur-${row.id}`}>{`no € price: ${sizes.join(", ")}`}</Badge>;
};

export default function CataloguePage() {
  const table = useTable("/admin/tables/products");
  const options = useFilterOptions();
  const [msg, setMsg] = useState("");
  const [editId, setEditId] = useState(null);
  const [creating, setCreating] = useState(false);

  const columns = [
    { key: "title", label: "Bouquet", render: (r) => (
      <span className="flex items-center gap-2">
        {r.image
          ? <ZoomImage src={media(r.image)} alt={r.title} caption={`${r.title} · ${r.city || ""}`.trim()}
                       className="w-9 aspect-[4/5] shrink-0" testid={`catalogue-photo-${r.id}`} />
          : <span className="w-9 aspect-[4/5] bg-plaster-deep" />}
        <span>{r.title}<span className="block text-xs text-muted">{r.city}</span></span>
      </span>
    ) },
    { key: "enabled_partner_count", label: "Active partners", render: (r) => <span data-testid={`catalogue-partners-${r.id}`}>{r.enabled_partner_count || 0}</span> },
    { key: "status", label: "Status", sortable: true, render: (r) => <Badge tone={statusTone(r.status)}>{(r.status || "").replace(/_/g, " ")}</Badge> },
    { key: "cost_price_mad", label: "Cost", render: (r) => <span className="tabular whitespace-nowrap text-muted">{r.cost_price_mad != null ? `${money(r.cost_price_mad)} MAD` : "—"}</span> },
    { key: "base_price_eur", label: "Selling EUR", sortable: true, render: (r) => (
      <span className="whitespace-nowrap">
        <span className="tabular" data-testid={`catalogue-retail-${r.id}`}>€{Number(r.base_price_eur || 0).toFixed(2)}</span>
        {r.needs_selling_price ? <Badge tone="warn" testid={`needs-price-${r.id}`}>set your selling price</Badge> : null}
        <MissingEurBadge row={r} />
      </span>
    ) },
    { key: "margin_mad", label: "Margin", render: (r) => (
      r.margin_mad == null ? "—" : <span className={`tabular whitespace-nowrap ${r.margin_mad <= 0 ? "text-danger" : "text-success"}`} data-testid={`margin-${r.id}`}>{r.margin_mad > 0 ? "+" : ""}{money(r.margin_mad)} MAD</span>
    ) },
    { key: "sales_count", label: "Sold", sortable: true, render: (r) => <span className="tabular">{r.sales_count}</span> },
    { key: "seo_complete", label: "SEO", render: (r) => <Badge tone={r.seo_complete ? "good" : "warn"}>{r.seo_complete ? "written" : "missing"}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <button className="btn-ghost text-[11px]" data-testid={`edit-${r.id}`} onClick={() => setEditId(r.id)}>Edit</button>
    ) },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink mb-1">Bouquets & prices</h1>
          <p className="text-sm text-muted">Fleurinelle owns every bouquet. Cost = what the partner charges us · Selling = what the buyer pays.</p>
        </div>
        <button className="btn-primary text-xs px-4 inline-flex items-center gap-1.5" data-testid="catalogue-add-bouquet" onClick={() => setCreating(true)}>
          <Plus size={14} /> Add bouquet
        </button>
      </div>
      {msg ? <p className="text-sm text-brass" data-testid="catalogue-message">{msg}</p> : null}
      <PriceHealthBanner count={table.data?.missing_eur || 0} active={!!table.params.price_issue}
                         onShow={() => table.set({ price_issue: "1" })} onClear={() => table.set({ price_issue: "" })} />
      <DataTable table={table} columns={columns} testid="catalogue-table" name="catalogue"
                 searchPlaceholder="Bouquet name…" empty="No bouquet matches those filters."
                 filters={(
                   <>
                     <Select testid="catalogue-filter-status" placeholder="Any status" value={table.params.status} onChange={(v) => table.set({ status: v })}
                             options={["published", "draft", "paused", "archived"]} />
                     <Select testid="catalogue-filter-city" placeholder="Any city" value={table.params.city_id} onChange={(v) => table.set({ city_id: v })}
                             options={options.cities.map((c) => ({ value: c.id, label: c.name }))} />
                     <Select testid="catalogue-filter-shop" placeholder="Any partner" value={table.params.florist_id} onChange={(v) => table.set({ florist_id: v })}
                             options={options.florists.map((f) => ({ value: f.id, label: f.name }))} />
                   </>
                 )} />
      {creating ? (
        <NewBouquetModal partners={options.florists || []} onClose={() => setCreating(false)}
                         onCreated={(id) => { setCreating(false); setMsg("Draft created — add photos and publish."); table.reload(); setEditId(id); }} />
      ) : null}
      {editId ? (
        <AdminProductEditor
          productId={editId}
          occasions={options.occasions || []}
          productTypes={options.product_types || []}
          partners={options.florists || []}
          onClose={() => setEditId(null)}
          onSaved={(done) => { setMsg(done); table.reload(); }}
        />
      ) : null}
    </div>
  );
}
